import React from 'react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts'

import ChartTooltip from './ChartTooltip'

/**
 * Daily activity per entity, with a crosshair tooltip.
 *
 * Entity colours match the leaderboard, so a hue means the same thing on both.
 * Only entities that actually appear in the range get a line -- an always-seven
 * legend of mostly flat zeroes reads as noise.
 *
 * A series may also carry `dashed: true` (see `seriesForChart`'s
 * `colorBy: 'entityPerAction'` branch), which renders as a dashed line
 * instead of a second colour -- how a chart with more actions than the
 * palette has flat colours for (the incidents daily trend: five entities x
 * two modes) still keeps every series distinguishable.
 *
 * Two knobs make that dashing actually visible, both scoped to series that
 * carry the `dashed` flag (`item.dashed !== undefined`) so every other
 * report's trend renders exactly as before:
 *
 * - `legendType` switches to `'plainline'`. recharts' default line-legend
 *   icon (`'line'`) is a decorative wave that ignores `strokeDasharray`
 *   entirely, so without this the legend would show ten identical-looking
 *   wavy marks despite the chart itself alternating solid and dashed.
 *   `'plainline'` draws a straight segment that honours the dash pattern
 *   instead, so the legend actually reads as two different things.
 *
 * - `isAnimationActive` turns off. recharts' line-draw animation computes
 *   its own `strokeDasharray` every frame (an SVG dash-offset trick used to
 *   draw the line progressively) and overwrites whatever this component
 *   passed in, landing on a "no gaps" pattern once the animation completes
 *   -- solid, regardless of the `'6 4'` set below. The legend icon is
 *   unaffected (it reads the static prop, not the animated line), but the
 *   plotted line itself would silently render solid either way without this.
 */
const TrendLine = ({ data, series, height = 320, showLegend = true }) => (
    <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#e1e0d9" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#898781' }} stroke="#c3c2b7" minTickGap={16} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#898781' }} stroke="#c3c2b7" width={44} />
            <Tooltip content={<ChartTooltip total />} cursor={{ stroke: '#c3c2b7', strokeWidth: 1 }} />
            {showLegend && <Legend wrapperStyle={{ fontSize: 12 }} />}
            {series.map((item) => {
                const hasStyleMarker = item.dashed !== undefined
                return (
                    <Line
                        key={item.key}
                        type="monotone"
                        dataKey={item.key}
                        name={item.label}
                        stroke={item.color}
                        strokeWidth={2}
                        strokeDasharray={item.dashed ? '6 4' : undefined}
                        legendType={hasStyleMarker ? 'plainline' : 'line'}
                        isAnimationActive={hasStyleMarker ? false : undefined}
                        dot={false}
                        activeDot={{ r: 4, strokeWidth: 2, stroke: '#ffffff' }}
                    />
                )
            })}
        </LineChart>
    </ResponsiveContainer>
)

export default TrendLine
